import { injectable, inject } from '@servicetitan/react-ioc';
import { SettingsApi } from 'api';
import { FeaturesApi } from 'api/src/features-api';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { getNormalizedFilter } from '../../../utils/settings';

enum SpecialSettingsKeys {
    ProposalsEstimatesSettings = 'proposals-estimates-settings',
    ServiceAgreementsSettings = 'service-agreements-settings',
    MembershipSettings = 'membership-settings',
    DirectLinksSettings = 'direct-links-settings',
}

export interface ProposalsEstimatesSettings {
    enabled: boolean;
}

export interface ServiceAgreementsSettings {
    enabled: boolean;
}

export interface MembershipSettings {
    enabled: boolean;
}

export interface DirectLinksSettings {
    allowUserSignUpSignIn: boolean;
}

@injectable()
export class AppSettingsStore {
    @observable loadingSettings = false;
    @observable proposalsEstimatesSettings: ProposalsEstimatesSettings = {
        enabled: false,
    };
    @observable serviceAgreementsSettings: ServiceAgreementsSettings = {
        enabled: false,
    };

    @observable membershipSettings: MembershipSettings = {
        enabled: false,
    };

    @observable directLinksSettings: DirectLinksSettings = {
        allowUserSignUpSignIn: true,
    };

    private serviceAgreementsFGEnabled = false;

    constructor(
        @inject(SettingsApi) private settingsApi: SettingsApi,
        @inject(FeaturesApi) private featuresApi: FeaturesApi
    ) {
        makeObservable(this);

        this.loadSettings();
    }

    @computed get proposalsEstimatesEnabled() {
        return !!this.proposalsEstimatesSettings.enabled;
    }

    @computed get serviceAgreementsEnabled() {
        return !!this.serviceAgreementsSettings.enabled && this.serviceAgreementsFGEnabled;
    }

    @computed get membershipEnabled() {
        return !!this.membershipSettings.enabled;
    }

    @computed get directLinksSignUpSignInEnabled() {
        return !!this.directLinksSettings.allowUserSignUpSignIn;
    }

    @action
    private async loadSettings() {
        this.loadingSettings = true;

        try {
            const [
                estimateResponse,
                saResponse,
                membershipSettingsResponse,
                directLinksSettingsResponse,
                features,
            ] = await Promise.allSettled([
                this.settingsApi.client.getSpecialSettings(
                    SpecialSettingsKeys.ProposalsEstimatesSettings
                ),
                this.settingsApi.client.getSpecialSettings(
                    SpecialSettingsKeys.ServiceAgreementsSettings
                ),
                this.settingsApi.client.getSpecialSettings(SpecialSettingsKeys.MembershipSettings),
                this.settingsApi.client.getSpecialSettings(SpecialSettingsKeys.DirectLinksSettings),
                this.featuresApi.getFeatureGates(),
            ]);

            runInAction(() => {
                if (estimateResponse.status === 'fulfilled') {
                    this.proposalsEstimatesSettings = {
                        ...this.proposalsEstimatesSettings,
                        ...getNormalizedFilter(estimateResponse.value),
                    };
                }
                if (saResponse.status === 'fulfilled') {
                    this.serviceAgreementsSettings = {
                        ...this.serviceAgreementsSettings,
                        ...getNormalizedFilter(saResponse.value),
                    };
                }
                if (membershipSettingsResponse.status === 'fulfilled') {
                    this.membershipSettings = {
                        ...this.membershipSettings,
                        ...getNormalizedFilter(membershipSettingsResponse.value),
                    };
                }
                if (directLinksSettingsResponse.status === 'fulfilled') {
                    this.directLinksSettings = {
                        ...this.directLinksSettings,
                        ...getNormalizedFilter(directLinksSettingsResponse.value),
                    };
                }
                if (features.status === 'fulfilled') {
                    const fg = features.value.enabledFeatureGates;
                    this.serviceAgreementsFGEnabled = !!fg?.includes('ServiceAgreements');
                }
            });
        } finally {
            runInAction(() => {
                this.loadingSettings = false;
            });
        }
    }
}
